import React from 'react';
import { Facebook, Instagram } from 'lucide-react';
import { heroData } from '../data/mockData';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';

const HeroSection = () => {
  const handleClick = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative pt-32 pb-16 md:pt-48 md:pb-24 lg:pt-56 lg:pb-32 overflow-hidden flex flex-col justify-center"
    >
      {/* Glow effect */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-primary/10 blur-[120px] rounded-full pointer-events-none z-0" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 w-full text-center flex flex-col items-center">
        <AnimateOnScroll>
          <span className="inline-block py-1 px-3 rounded-full bg-white/5 border border-white/10 text-primary text-xs sm:text-sm font-bold tracking-widest uppercase mb-6">
            {heroData.badge}
          </span>
        </AnimateOnScroll>

        <AnimateOnScroll delay={100}>
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white leading-[1.2] sm:leading-[1.1] mb-6 w-full max-w-4xl mx-auto italic">
            {heroData.headingLine1}
            <br className="hidden sm:block" />
            {heroData.headingLine2}
          </h1>
        </AnimateOnScroll>

        <AnimateOnScroll delay={200}>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            {heroData.subtitle}
          </p>
        </AnimateOnScroll>

        <AnimateOnScroll delay={300}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
            <a
              href={heroData.ctaPrimary.href}
              onClick={(e) => handleClick(e, heroData.ctaPrimary.href)}
              className="w-full sm:w-auto px-8 py-4 bg-primary text-black font-bold rounded-full hover:bg-yellow-400 transition-all hover:scale-105 shadow-[0_0_20px_rgba(212,175,55,0.3)] text-sm sm:text-base"
            >
              {heroData.ctaPrimary.label}
            </a>
            <a
              href={heroData.ctaSecondary.href}
              onClick={(e) => handleClick(e, heroData.ctaSecondary.href)}
              className="w-full sm:w-auto px-8 py-4 border border-white/10 bg-white/5 text-white font-bold rounded-full hover:bg-white/10 transition-all text-sm sm:text-base"
            >
              {heroData.ctaSecondary.label}
            </a>
          </div>

          {/* Social Icons */}
          <div className="flex items-center justify-center gap-5 mt-8">
            <span className="text-zinc-400 text-sm font-medium">Check us on here:</span>
            <a
              href="https://www.facebook.com/goldenscissorsmarketharborough"
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-zinc-300 hover:text-primary hover:border-primary/40 hover:bg-primary/10 transition-all hover:scale-110 hover:shadow-[0_0_15px_rgba(212,175,55,0.2)]"
              aria-label="Facebook"
            >
              <Facebook className="w-7 h-7" />
            </a>
            <a
              href="https://www.instagram.com/goldenscissorsharborough/?hl=en"
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-zinc-300 hover:text-primary hover:border-primary/40 hover:bg-primary/10 transition-all hover:scale-110 hover:shadow-[0_0_15px_rgba(212,175,55,0.2)]"
              aria-label="Instagram"
            >
              <Instagram className="w-7 h-7" />
            </a>
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll delay={400}>
          <div className="mt-16 md:mt-24 w-full max-w-6xl mx-auto relative group">
            {/* Gradient fade at bottom */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-transparent z-10" />
            {/* Glow border */}
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-transparent rounded-[2rem] blur-lg opacity-50 group-hover:opacity-100 transition-opacity duration-700" />
            <img
              src={heroData.image}
              alt={heroData.imageAlt}
              className="relative w-full h-[350px] sm:h-[450px] md:h-[600px] object-cover rounded-3xl border border-white/10 z-0 grayscale-[20%] group-hover:grayscale-0 transition-all duration-700"
            />
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default HeroSection;
